//
// # LvsPanel
//
// Panel showing LVS (Layout vs Schematic) verification results.
// Displays errors grouped by type with click-to-highlight functionality.
//
// Click an error row to highlight the relevant layout elements.
//

import React, { useEffect, useState } from "react";
import {
  AlertTriangle,
  CheckCircle,
  XCircle,
  EyeOff,
  Play,
} from "lucide-react";
import { useWorkspace, useWorkspaceSnapshot } from "../../context";
import { useLvsResults } from "./useLvsResults";
import { LvsErrorRow } from "./LvsErrorRow";
import "./lvsPanel.css";

// Format identKey for display: {"kind":"hierpath","path":["lib","cell","view"]} -> "lib/cell/view"
function formatIdentKey(identKey: string): string {
  try {
    const parsed = JSON.parse(identKey) as { path?: string[] };
    if (parsed.path && Array.isArray(parsed.path)) {
      return parsed.path.join("/");
    }
  } catch {
    // Fall through to return as-is
  }
  return identKey;
}

export function LvsPanel() {
  const workspace = useWorkspace();
  const snapshot = useWorkspaceSnapshot();
  const { result, loading } = useLvsResults();
  const [running, setRunning] = useState(false);

  // Clear highlights when panel unmounts
  useEffect(() => {
    return () => {
      workspace.highlightLvsError(null);
    };
  }, [workspace]);

  const handleClearHighlights = () => {
    workspace.highlightLvsError(null);
  };

  const handleRunLvs = async (mode: "name_based" | "topology_based") => {
    setRunning(true);
    try {
      // Result is intentionally not checked here - workspace.runLvs displays
      // errors via the status bar, and LVS results arrive via WebSocket broadcast.
      await workspace.runLvs(mode);
    } finally {
      setRunning(false);
    }
  };

  // Count errors by type
  const errorCounts = result?.errors?.reduce((acc, err) => {
    acc[err.kind] = (acc[err.kind] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const totalErrors = result?.errors?.length ?? 0;
  const passed = result?.passed ?? false;

  // Check if active editor is a layout
  const isLayoutActive =
    snapshot.activeIdent &&
    workspace.getActiveEditor()?.content.contentKind === "layout";

  const isRunning = running || loading;

  const lvsButtonClass =
    "flex-1 px-3 py-1.5 rounded flex items-center justify-center gap-1.5 text-[11px] " +
    "bg-[var(--color-ide-surface-hover)] hover:bg-[var(--color-ide-surface-selected)] " +
    "text-[var(--color-ide-text)] disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

  return (
    <div className="flex flex-col h-full bg-[var(--color-ide-surface)] text-[var(--color-ide-text)]">
      {/* Panel header */}
      <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-widest text-[var(--color-ide-text-muted)] select-none border-b border-[var(--color-ide-border)]">
        <div className="flex items-center gap-1.5">
          <AlertTriangle size={11} />
          LVS Results
          {result?.identKey && (
            <span className="ml-1 font-normal normal-case tracking-normal text-[var(--color-ide-text-dim)]">
              — {formatIdentKey(result.identKey)}
            </span>
          )}
        </div>
      </div>

      {/* Content area */}
      <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden">
        {/* Run LVS button */}
        {isLayoutActive && (
          <div className="p-2 flex gap-2 border-b border-[var(--color-ide-border)]">
            <button
              className={lvsButtonClass}
              onClick={() => handleRunLvs("name_based")}
              disabled={isRunning}
              title="Run Layout vs Schematic verification"
            >
              <Play size={12} />
              Run LVS
            </button>
          </div>
        )}

        {isRunning && (
          <div className="p-3 text-[11px] text-[var(--color-ide-text-muted)]">
            Running LVS...
          </div>
        )}

        {!isLayoutActive && (
          <div className="p-3 text-[11px] text-[var(--color-ide-text-dim)]">
            Open a layout to run LVS.
          </div>
        )}

        {isLayoutActive && !isRunning && !result && (
          <div className="p-3 text-[11px] text-[var(--color-ide-text-dim)]">
            No LVS results. Click the button above to verify layout against
            schematic.
          </div>
        )}

        {!isRunning && result && (
          <div className="p-2">
            {/* Status banner */}
            <div
              className={`px-3 py-2 mb-2 rounded flex items-center gap-2 text-[11px] ${
                passed
                  ? "bg-green-500/10 text-green-400"
                  : "bg-red-500/10 text-red-400"
              }`}
            >
              {passed ? <CheckCircle size={14} /> : <XCircle size={14} />}
              {passed
                ? "LVS Clean"
                : `${totalErrors} Error${totalErrors !== 1 ? "s" : ""} Found`}
            </div>

            {/* Error summary */}
            {!passed && errorCounts && (
              <div className="px-2 pb-2 text-[10px] text-[var(--color-ide-text-muted)] flex flex-wrap gap-2">
                {errorCounts.open && (
                  <span className="px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">
                    {errorCounts.open} Open{errorCounts.open !== 1 ? "s" : ""}
                  </span>
                )}
                {errorCounts.short && (
                  <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">
                    {errorCounts.short} Short
                    {errorCounts.short !== 1 ? "s" : ""}
                  </span>
                )}
                {errorCounts.missing_instance && (
                  <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                    {errorCounts.missing_instance} Missing Instance
                    {errorCounts.missing_instance !== 1 ? "s" : ""}
                  </span>
                )}
                {errorCounts.extra_instance && (
                  <span className="px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400">
                    {errorCounts.extra_instance} Extra Instance
                    {errorCounts.extra_instance !== 1 ? "s" : ""}
                  </span>
                )}
                {errorCounts.missing_pin && (
                  <span className="px-1.5 py-0.5 rounded bg-purple-500/20 text-purple-400">
                    {errorCounts.missing_pin} Missing Pin
                    {errorCounts.missing_pin !== 1 ? "s" : ""}
                  </span>
                )}
                {errorCounts.extra_pin && (
                  <span className="px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-400">
                    {errorCounts.extra_pin} Extra Pin
                    {errorCounts.extra_pin !== 1 ? "s" : ""}
                  </span>
                )}
                {/* Topology-based errors */}
                {errorCounts.unmatched_schematic_net && (
                  <span className="px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">
                    {errorCounts.unmatched_schematic_net} Unmatched Sch Net
                    {errorCounts.unmatched_schematic_net !== 1 ? "s" : ""}
                  </span>
                )}
                {errorCounts.unmatched_layout_net && (
                  <span className="px-1.5 py-0.5 rounded bg-orange-500/20 text-orange-400">
                    {errorCounts.unmatched_layout_net} Unmatched Lay Net
                    {errorCounts.unmatched_layout_net !== 1 ? "s" : ""}
                  </span>
                )}
                {errorCounts.unmatched_schematic_device && (
                  <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                    {errorCounts.unmatched_schematic_device} Unmatched Sch
                    Device
                    {errorCounts.unmatched_schematic_device !== 1 ? "s" : ""}
                  </span>
                )}
                {errorCounts.unmatched_layout_device && (
                  <span className="px-1.5 py-0.5 rounded bg-yellow-500/20 text-yellow-400">
                    {errorCounts.unmatched_layout_device} Unmatched Lay Device
                    {errorCounts.unmatched_layout_device !== 1 ? "s" : ""}
                  </span>
                )}
                {errorCounts.structural_mismatch && (
                  <span className="px-1.5 py-0.5 rounded bg-red-500/20 text-red-400">
                    {errorCounts.structural_mismatch} Structural Mismatch
                    {errorCounts.structural_mismatch !== 1 ? "es" : ""}
                  </span>
                )}
              </div>
            )}

            {/* Error list */}
            {result.errors && result.errors.length > 0 && (
              <div className="flex flex-col gap-1">
                {/* Clear highlights button */}
                <button
                  className="w-full px-2 py-1 rounded flex items-center gap-2 text-[10px] text-[var(--color-ide-text-muted)] hover:bg-[var(--color-ide-surface-hover)] transition-colors"
                  onClick={handleClearHighlights}
                >
                  <EyeOff size={10} />
                  Clear highlights
                </button>
                {result.errors.map((error, index) => (
                  <LvsErrorRow key={index} error={error} index={index} />
                ))}
              </div>
            )}

            {/* Stats */}
            {result.stats && (
              <div className="mt-3 px-2 text-[10px] text-[var(--color-ide-text-dim)]">
                <div>
                  Schematic: {result.stats.schematicNetCount ?? 0} nets,{" "}
                  {result.stats.schematicInstanceCount ?? 0} instances
                </div>
                <div>
                  Layout: {result.stats.layoutConnCount ?? 0} nets,{" "}
                  {result.stats.layoutInstanceCount ?? 0} instances
                </div>
                <div>
                  Matched:{" "}
                  {result.stats.topologyMatchedNetCount ??
                    result.stats.matchedNetCount ??
                    0}{" "}
                  nets, {result.stats.matchedDeviceCount ?? 0} devices
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
